/**
 * The owner's bar setups, measured over the chart's own bars
 * (`docs/plans/bar-highlights.md`). Two so far, both long, both three bars:
 *
 * **Inside bar** (2026-10-13)
 * ```
 * IF Inside Bar And Range <= Range.1 * .9 And Range >= Range.1 * .1
 * THEN BUY Next Bar at High + entry STOP        (entry: a setting, $0.10)
 * stop: the inside bar's low
 * ```
 *
 * **Outside bar** (2026-10-13)
 * ```
 * setup:   an outside bar
 * trigger: the next bar only, closing above the outside bar's high
 * entry:   market buy at that close (a bar that clears the high and closes
 *          back under it is the fakeout the rule exists to skip)
 * stop:    the outside bar's low
 * ```
 *
 * No target was given for either, so each trade reports **how far it got
 * before the stop**, in R (1R = fill − stop), and the panel counts 1R, 2R, 3R.
 *
 * Fills follow the paper-fill simulator (`journal/simulator.py`): a stop entry
 * fills into whatever market it triggered — a bar opening above it fills at its
 * open — and a bar that touches the stop is the stop, even if its high went
 * further, because daily bars cannot say which came first.
 */

/** The inside rule's two range bounds, as the owner wrote them: 10% to 90% of the bar before. */
export const RANGE_MAX = 0.9;
export const RANGE_MIN = 0.1;
/** The default distance above the inside bar's high, in dollars (owner). */
export const DEFAULT_ENTRY = 0.1;
/** The R levels the panel counts. */
export const R_LEVELS = [1, 2, 3] as const;

export interface SetupBars {
  t: readonly number[];
  o?: readonly number[];
  h: readonly number[];
  l: readonly number[];
  c?: readonly number[];
}

export interface SetupTrade {
  /** The setup bar's time. */
  t: number;
  triggered: boolean;
  fill?: number;
  stop: number;
  /** The furthest the high went above the fill before the stop, in R; 0 at worst. */
  maxR?: number;
  /** The stop was hit. */
  stopped?: boolean;
}

export interface SetupStats {
  bars: number;
  setups: number;
  triggered: number;
  /** Stopped before reaching 1R. */
  stopped: number;
  /** Triggered trades whose high reached each of `R_LEVELS` before the stop. */
  reached: number[];
  /** Neither stopped nor at 1R by the last settled bar. */
  open: number;
  /** Mean `maxR` over triggered trades; undefined when none triggered. */
  avgMaxR?: number;
  /** The most recent setup, whatever happened to it. */
  last?: SetupTrade;
}

const finite = (...xs: number[]) => xs.every(Number.isFinite);

/** Whether bar `i` is an inside setup: strictly inside the bar before, range 10%–90% of its range. */
export function isSetup(bars: SetupBars, i: number): boolean {
  if (i < 1) return false;
  const h = bars.h[i]!;
  const l = bars.l[i]!;
  const ph = bars.h[i - 1]!;
  const pl = bars.l[i - 1]!;
  if (!finite(h, l, ph, pl)) return false;
  if (!(h < ph && l > pl)) return false;
  const range = h - l;
  const prev = ph - pl;
  return range <= prev * RANGE_MAX && range >= prev * RANGE_MIN;
}

/** Whether bar `i` is an outside setup: strictly engulfs the bar before. */
export function isOutsideSetup(bars: SetupBars, i: number): boolean {
  if (i < 1) return false;
  const h = bars.h[i]!;
  const l = bars.l[i]!;
  const ph = bars.h[i - 1]!;
  const pl = bars.l[i - 1]!;
  return finite(h, l, ph, pl) && h > ph && l < pl;
}

/**
 * From a fill on bar `from`, the run before the stop. `includeFrom` says
 * whether bar `from` itself is after the fill: true for a stop entry (the
 * high and low of the entry bar can come after it), false for a fill at the
 * close (nothing on that bar comes after).
 */
function follow(
  bars: SetupBars,
  t: number,
  from: number,
  includeFrom: boolean,
  settled: number,
  fill: number,
  stop: number,
): SetupTrade {
  const risk = fill - stop;
  let best = 0;
  for (let k = includeFrom ? from : from + 1; k < settled; k++) {
    if (bars.l[k]! <= stop) return { t, triggered: true, fill, stop, maxR: best, stopped: true };
    if (risk > 0) best = Math.max(best, (bars.h[k]! - fill) / risk);
  }
  return { t, triggered: true, fill, stop, maxR: best, stopped: false };
}

/** One inside setup's trade: a buy stop on the next bar at high + `entry`. */
export function followSetup(bars: SetupBars, i: number, settled: number, entry: number): SetupTrade {
  const stop = bars.l[i]!;
  const price = bars.h[i]! + entry;
  const j = i + 1;
  if (j >= settled || !(bars.h[j]! >= price)) return { t: bars.t[i]!, triggered: false, stop };
  const open = bars.o?.[j];
  const fill = open !== undefined && Number.isFinite(open) && open > price ? open : price;
  return follow(bars, bars.t[i]!, j, true, settled, fill, stop);
}

/** One outside setup's trade: the next bar must close above the high; bought at that close. */
export function followOutsideSetup(bars: SetupBars, i: number, settled: number): SetupTrade {
  const stop = bars.l[i]!;
  const j = i + 1;
  const close = bars.c?.[j];
  if (j >= settled || close === undefined || !Number.isFinite(close) || !(close > bars.h[i]!)) {
    return { t: bars.t[i]!, triggered: false, stop };
  }
  return follow(bars, bars.t[i]!, j, false, settled, close, stop);
}

function stats(
  bars: SetupBars,
  settled: number,
  find: (i: number) => boolean,
  trade: (i: number, n: number) => SetupTrade,
): SetupStats {
  const n = Math.min(settled, bars.t.length, bars.h.length, bars.l.length);
  const out: SetupStats = { bars: n, setups: 0, triggered: 0, stopped: 0, reached: R_LEVELS.map(() => 0), open: 0 };
  let sumR = 0;
  for (let i = 1; i < n; i++) {
    if (!find(i)) continue;
    const tr = trade(i, n);
    out.setups++;
    out.last = tr;
    if (!tr.triggered) continue;
    out.triggered++;
    const r = tr.maxR ?? 0;
    sumR += r;
    R_LEVELS.forEach((level, at) => {
      if (r >= level) out.reached[at]!++;
    });
    if (r < R_LEVELS[0]) {
      if (tr.stopped) out.stopped++;
      else out.open++;
    }
  }
  if (out.triggered > 0) out.avgMaxR = sumR / out.triggered;
  return out;
}

/** Every inside setup among the first `settled` bars (a forming last bar is left out). */
export function insideSetupStats(bars: SetupBars, settled: number, entry: number = DEFAULT_ENTRY): SetupStats {
  return stats(bars, settled, (i) => isSetup(bars, i), (i, n) => followSetup(bars, i, n, entry));
}

/** Every outside setup among the first `settled` bars. */
export function outsideSetupStats(bars: SetupBars, settled: number): SetupStats {
  return stats(bars, settled, (i) => isOutsideSetup(bars, i), (i, n) => followOutsideSetup(bars, i, n));
}
